import { BLOCKING_ERROR_TYPE } from "@/apis/constants";
import { AI_AUTOMATION_ATTEMPTED_STORAGE_KEY } from "@/components/AIAutomation/constants";
import {
  HelpMsgLocaleId,
  RefPageValue,
} from "@/components/GetSupport/ChatWithITSupport/types";
import { Day1EventLabel } from "@/types/snowplow-events";

/**
 * Get the ref_page value that will be included in the IT support link for the given blocking error type on Day1.
 * @param errorType The blocking error type on Day1.
 */
export const getRefPageByError = (
  errorType: BLOCKING_ERROR_TYPE
): RefPageValue => {
  switch (errorType) {
    case BLOCKING_ERROR_TYPE.BeforeDay1:
    case BLOCKING_ERROR_TYPE.NotDay1:
      return "ezo-blocking-error-100";
    case BLOCKING_ERROR_TYPE.NoAlias:
      return "ezo-blocking-error-200";
    case BLOCKING_ERROR_TYPE.IntentionalDenial:
      return "ezo-blocking-error-300";
    case BLOCKING_ERROR_TYPE.MissingData:
      return "ezo-blocking-error-400";
    case BLOCKING_ERROR_TYPE.InvalidCountryCode:
      return "ezo-blocking-error-500";
    case BLOCKING_ERROR_TYPE.InvalidJobLevel:
      return "ezo-blocking-error-600";
    case BLOCKING_ERROR_TYPE.EzoDisabled:
      return "ezo-blocking-error-700";
    default:
      return "ezo-blocking-error-999";
  }
};

/**
 * Get the help message that will be included in the IT support link by the given ref-page.
 * @param refPage The ref-page value used in the IT support link.
 */
export const getHelpMsgLocaleIdByRefPage = (
  refPage?: RefPageValue
): HelpMsgLocaleId | undefined => {
  if (!refPage) {
    return undefined;
  }

  switch (refPage) {
    case "ezo-biometric-preference-contact-card":
    case "ezo-identity-check-chime-verification":
      return "itHelpMsg-contactSupportOnboardThroughChime";
    case "ezo-blocking-error-100":
    case "ezo-blocking-error-200":
    case "ezo-blocking-error-300":
    case "ezo-blocking-error-400":
    case "ezo-blocking-error-500":
    case "ezo-blocking-error-600":
    case "ezo-blocking-error-700":
    case "ezo-blocking-error-999":
      // The cause-specific blocking-error ref-pages added in NHE-5600 all
      // route to the same generic whisper copy until product team provides
      // refined messaging per category.
      return "itHelpMsg-day1BlockingIssue";
    case "ezo-identity-check":
    case "ezo-identity-check-manual-full":
    case "ezo-identity-check-manual-full-pri":
    case "ezo-identity-check-manual-full-sec":
    case "ezo-identity-check-manual-full-challenge":
    case "ezo-identity-check-manual-full-challenge-unable-to-complete-challenge":
    case "ezo-identity-check-manual-full-wait":
    case "ezo-identity-check-manual-lightweight-wait":
    case "ezo-identity-check-automated-failed":
    case "ezo-identity-check-automated-failed-with-fallback":
    case "ezo-identity-check-automated-failed-no-fallback":
      return "itHelpMsg-verificationIssueAndAssist";
    case "ezo-identity-check-manual-full-pass":
    case "ezo-identity-check-manual-lightweight":
      return "itHelpMsg-verificationIssue";
    case "ezo-identity-check-manual-lightweight-pass":
      return "itHelpMsg-settingUpLaptop";
    case "ezo-identity-check-manual-full-fail":
    case "ezo-identity-check-manual-lightweight-challenge-unable-to-complete-challenge":
    case "ezo-identity-check-manual-lightweight-fail":
      return "itHelpMsg-verificationAssist";
    case "ezo-amazon-username":
      return "itHelpMsg-usernameIssue";
    case "ezo-insert-and-test-sec-key":
      // TODO: [Ask Product if we should have an IT Support help message for insert and test security key](https://taskei.amazon.dev/tasks/NHE-5123)
      return undefined;
    case "ezo-midway-detection-timeout":
    case "ezo-midway-register-sec-key":
      return "itHelpMsg-midwayAssist";
    case "ezo-mw-error-100":
      return "itHelpMsg-midwayPinIssue";
    case "ezo-mw-error-200":
      return "itHelpMsg-midwayEmailIssue";
    case "ezo-mw-error-300":
      return "itHelpMsg-midwayDateIssue";
    case "ezo-mw-error-429":
      return "itHelpMsg-midwayRateLimitIssue";
    case "ezo-mw-error-999":
      return "itHelpMsg-midwayIssue";
    case "ezo-midway-register-sec-key-after-temp-pin":
      return "itHelpMsg-midwayIssue";
    case "ezo-connect-to-vpn":
      return "itHelpMsg-vpnIssue";
    case "ezo-create-amazon-password":
      return "itHelpMsg-passwordIssue";
    case "ezo-install-precache":
      return "itHelpMsg-preCacheADIssue";
    case "ezo-amazon-account-cache":
      // Windows account caching reuses the Pre-Cache AD whisper copy since it
      // represents the same concept (account caching) for IT support context.
      return "itHelpMsg-preCacheADIssue";
    case "ezo-identity-check-automated-consent":
    case "ezo-identity-check-manual-consent":
    case "ezo-identity-check-automated-in-progress":
    case "ezo-identity-check-select-preference":
    case "ezo-identity-check-automated-pass":
    case "ezo-identity-check-automated-expired":
    case "ezo-ai-consent":
    case "ezo-mac-permissions":
    case "ezo-complete":
    case "ezo-login":
      return undefined;
  }
};

/**
 * Returns true if the user has started AI automation in this browser session.
 */
export const getAiAutomationAttempted = (): boolean => {
  try {
    return localStorage.getItem(AI_AUTOMATION_ATTEMPTED_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
};

/**
 * Whether the chat handoff should offer a link to this hire's recorded session.
 *
 * A session-wide recording is worth reviewing from any step; the per-step cohort's is not.
 */
export const shouldOfferSessionReview = (
  isSessionReviewEnabled: boolean,
  helpMessageIntlId: HelpMsgLocaleId | undefined,
  hasJourneySession: boolean
): boolean =>
  isSessionReviewEnabled &&
  (hasJourneySession || helpMessageIntlId === "itHelpMsg-vpnIssue");

/**
 * Build the review session suffix to append to chat support messages.
 * Returns an empty string when disabled (EU region, PROD stage, or flag off) or
 * before the personId is known — omitting the link beats a dead one.
 */
export const getReviewSessionSuffix = (
  isEnabled: boolean,
  reviewBaseUrl: string,
  personId: string | undefined
): string => {
  if (!isEnabled || !personId) return "";
  return ` Review my onboarding session: ${reviewBaseUrl}/sessions/${personId}`;
};

/**
 * Get the Snowplow event label for the given blocking error type on Day1.
 * @param errorType The blocking error type on Day1.
 */
export const getMetricsLabelByError = (
  errorType: BLOCKING_ERROR_TYPE
): Day1EventLabel => {
  switch (errorType) {
    case BLOCKING_ERROR_TYPE.BeforeDay1:
    case BLOCKING_ERROR_TYPE.NotDay1:
      return Day1EventLabel.NotDay1;
    case BLOCKING_ERROR_TYPE.NoAlias:
    case BLOCKING_ERROR_TYPE.MissingData:
      return Day1EventLabel.AccountIssue;
    case BLOCKING_ERROR_TYPE.EzoDisabled:
    case BLOCKING_ERROR_TYPE.IntentionalDenial:
    case BLOCKING_ERROR_TYPE.InvalidCountryCode:
    case BLOCKING_ERROR_TYPE.InvalidJobLevel:
    default:
      return Day1EventLabel.GenericError;
  }
};
